import React, { useEffect, useMemo, useState } from 'react';
import Dialog from './TivigoDialog';
import CircularProgress from '@mui/material/CircularProgress';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import { ADULT_BOUQUET_IDS, normalizeBouquetResponse } from './UserPanelCategoryModal';
import TivigoToast from './TivigoToast';
import './userPanelAdultModal.css';

const normalizeIds = (values) => Array.from(new Set(
  (Array.isArray(values) ? values : [])
    .map((value) => String(value ?? '').trim())
    .filter(Boolean),
));

const getUsername = (userInfo, username) => {
  const info = userInfo?.user_info || {};
  const user = userInfo?.user || {};
  return username || info.username || user.username || userInfo?.username || 'Tivigo kullanıcısı';
};

const ensureUpdateSucceeded = (result) => {
  if (result?.success === false) {
    throw new Error(result.message || 'Yetişkin içerik ayarı güncellenemedi.');
  }
  return result;
};

function UserPanelAdultModal({
  open,
  onClose,
  userInfo,
  username,
  targetUserId = null,
  previewMode = false,
  onLoadBouquets,
  onUpdateBouquets,
  onSuccess,
}) {
  const accountName = getUsername(userInfo, username);
  const [userBouquetIds, setUserBouquetIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 3600);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setUserBouquetIds([]);
    setLoading(true);
    setSaving(false);
    setNotice(null);

    const load = async () => {
      try {
        const response = previewMode
          ? { all_bouquets: [], user_bouquets: [] }
          : typeof onLoadBouquets === 'function'
            ? await onLoadBouquets({ userInfo, targetUserId })
            : null;

        if (!response) {
          throw new Error('Kategori listeleme bağlantısı henüz sağlanmadı.');
        }

        const normalized = normalizeBouquetResponse(response);
        if (!cancelled) setUserBouquetIds(normalized.userBouquetIds);
      } catch (error) {
        if (!cancelled) {
          setNotice({ type: 'error', text: error.message || 'Yetişkin içerik durumu alınamadı.' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [onLoadBouquets, open, previewMode, targetUserId, userInfo]);

  const selectedSet = useMemo(() => new Set(normalizeIds(userBouquetIds)), [userBouquetIds]);
  const enabled = ADULT_BOUQUET_IDS.every((id) => selectedSet.has(id));
  const partial = !enabled && ADULT_BOUQUET_IDS.some((id) => selectedSet.has(id));

  const updateAdultSetting = async (checked) => {
    if (saving) return;

    const currentIds = normalizeIds(userBouquetIds);
    const currentSet = new Set(currentIds);
    const nextSet = new Set(currentIds);
    ADULT_BOUQUET_IDS.forEach((id) => {
      if (checked) nextSet.add(id);
      else nextSet.delete(id);
    });

    const nextIds = Array.from(nextSet);
    const toAdd = checked ? ADULT_BOUQUET_IDS.filter((id) => !currentSet.has(id)) : [];
    const toRemove = checked ? [] : ADULT_BOUQUET_IDS.filter((id) => currentSet.has(id));

    setSaving(true);
    setNotice(null);
    try {
      const result = previewMode
        ? { success: true, preview: true }
        : typeof onUpdateBouquets === 'function'
          ? ensureUpdateSucceeded(await onUpdateBouquets({
            userInfo,
            targetUserId,
            toAdd,
            toRemove,
            selectedBouquetIds: nextIds,
            originalBouquetIds: currentIds,
          }))
          : (() => { throw new Error('Kategori güncelleme bağlantısı henüz sağlanmadı.'); })();

      setUserBouquetIds(nextIds);
      setNotice({
        type: 'success',
        text: checked ? 'Yetişkin içerikler açıldı.' : 'Yetişkin içerikler kapatıldı.',
      });

      if (!previewMode && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('adultSettingsUpdated', {
          detail: { enabled: checked, preview: Boolean(result?.preview) },
        }));
      }

      if (!previewMode) {
        onSuccess?.({
          result,
          enabled: checked,
          selectedBouquetIds: nextIds,
          addedBouquetIds: toAdd,
          removedBouquetIds: toRemove,
        });
      }
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'Yetişkin içerik ayarı güncellenemedi.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      keepMounted
      transitionDuration={0}
      disableEscapeKeyDown
      className="tvup-adult-dialog"
      aria-labelledby="tvup-adult-modal-title"
      aria-describedby="tvup-adult-modal-description"
      slotProps={{
        backdrop: { className: 'tvup-adult-backdrop' },
        paper: { id: 'tvup-adult-modal', className: 'tvup-adult-modal' },
      }}
    >
      <header className="tvup-adult-header">
        <span className="tvup-adult-title-icon" aria-hidden="true"><SecurityRoundedIcon /></span>
        <div className="tvup-adult-title-copy">
          <h2 id="tvup-adult-modal-title">Yetişkin İçerikler</h2>
          <p id="tvup-adult-modal-description"><b>{accountName}</b> için erişim ayarını yönetin.</p>
        </div>
        <button type="button" className="tvup-adult-close" aria-label="Yetişkin içerik ayarları penceresini kapat" onClick={onClose}>
          <CloseRoundedIcon />
        </button>
      </header>

      <div className="tvup-adult-body">
        {loading ? (
          <div className="tvup-adult-loading" role="status">
            <CircularProgress size={30} thickness={4.5} />
            <b>İçerik durumu kontrol ediliyor</b>
            <span>Hesabınızın kategori izinleri okunuyor.</span>
          </div>
        ) : (
          <>
            <div className="tvup-adult-intro">
              <small>ERİŞİM KONTROLÜ</small>
              <h3>Yetişkin içeriklere erişimi tek noktadan yönetin.</h3>
              <p>Bu ayar, hesabınızla kullandığınız tüm cihazlarda geçerli olur.</p>
            </div>

            <div className={`tvup-adult-setting${enabled ? ' is-enabled' : ''}${partial ? ' is-partial' : ''}`}>
              <span className="tvup-adult-setting-icon"><SecurityRoundedIcon /></span>
              <span className="tvup-adult-setting-copy">
                <b>Yetişkin İçerikler</b>
                <small>
                  {enabled
                    ? 'Açık · Tüm yetişkin içeriklere erişiminiz var.'
                    : partial
                      ? 'Eksik ayar · İki içerik grubu birlikte güncellenecek.'
                      : 'Kapalı · Yetişkin içerikler hesabınızda kapalı.'}
                </small>
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label="Yetişkin içerikleri aç veya kapat"
                className={`tvup-adult-switch${enabled ? ' is-checked' : ''}`}
                disabled={saving}
                onClick={() => updateAdultSetting(!enabled)}
              >
                <span />
                {saving && <CircularProgress size={17} thickness={5} />}
              </button>
            </div>

            <TivigoToast notice={notice} />

            <div className="tvup-adult-info">
              <InfoOutlinedIcon />
              <p>Kategori değişikliğinden sonra güncel kanal listenize erişmek için uygulamanızı yeniden başlatın veya ayarlardan kanal listesini yenileyin. Yenileme seçeneği yoksa IPTV kurulumunu yeniden yapın.</p>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

export default UserPanelAdultModal;
